'use client';

import { useTheme } from 'next-themes';
import { useCallback, useEffect, useState } from 'react';

import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import {
  type MarketingAssetKey,
  marketingAssets,
} from '@/lib/marketing/assets';
import { theme } from '@/lib/ui/theme';

interface MediaSlotProps {
  assetKey: MarketingAssetKey;
  className?: string;
}

/**
 * Renders a marketing video/image, or a styled placeholder when the asset isn't
 * recorded yet. The reserved aspect ratio is identical in both states, so
 * filling in a real asset later causes zero layout shift. Image assets are
 * click-to-zoom (a full-screen lightbox), since a dense screenshot is unreadable
 * at slot size. See `@/lib/marketing/assets`.
 */
export function MediaSlot({ assetKey, className = '' }: MediaSlotProps) {
  const asset = marketingAssets[assetKey];
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
    };
    document.addEventListener('keydown', onKey);
    // Prevent background scroll while the lightbox is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxOpen, closeLightbox]);

  const isDark = mounted && resolvedTheme === 'dark';

  if (!asset.src) {
    return (
      <div
        className={`flex w-full items-center justify-center rounded-xl ${className}`}
        style={{
          aspectRatio: asset.aspect,
          background: 'transparent',
          border: `1px solid ${theme.box.purple.border[isDark ? 'dark' : 'light']}`,
        }}
        role="img"
        aria-label={asset.alt}
      >
        <p
          className="text-foreground/60 px-6 text-center text-sm md:text-base"
          style={{ fontFamily: theme.fonts.body }}
        >
          {asset.kind === 'video' ? '▶  ' : ''}
          {asset.label}
        </p>
      </div>
    );
  }

  if (asset.kind === 'video') {
    return (
      <video
        className={`w-full rounded-xl ${className}`}
        style={{ aspectRatio: asset.aspect }}
        controls
        preload="metadata"
        aria-label={asset.alt}
      >
        <source src={asset.src} />
      </video>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setLightboxOpen(true)}
        className={`group relative block w-full cursor-zoom-in overflow-hidden rounded-xl ${className}`}
        style={{ aspectRatio: asset.aspect }}
        aria-label={`${asset.alt} — click to enlarge`}
      >
        <img
          className="h-full w-full object-cover"
          style={{ objectPosition: asset.objectPosition ?? 'top' }}
          src={asset.src}
          alt={asset.alt}
          loading="lazy"
        />
        <span className="pointer-events-none absolute right-2 bottom-2 rounded-md bg-black/60 px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100">
          Click to enlarge
        </span>
      </button>

      {lightboxOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 md:p-8"
          role="dialog"
          aria-modal="true"
          aria-label={asset.alt}
          onClick={closeLightbox}
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 rounded-md bg-white/10 px-3 py-1 text-sm text-white hover:bg-white/20"
            aria-label="Close"
          >
            Close ✕
          </button>
          <img
            className="max-h-[90vh] max-w-[95vw] rounded-lg object-contain shadow-2xl"
            src={asset.src}
            alt={asset.alt}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
